// Pure business logic for the Schengen 90/180 rule.
// A "trip" object shape: { id: string, entry: 'YYYY-MM-DD', exit: 'YYYY-MM-DD' | null }
// null exit = open trip (still inside Schengen).
// Both the entry day and the exit day count as full days of stay.

import { addDays, daysBetween } from "./dateUtils";

const WINDOW_DAYS = 180;
const MAX_DAYS = 90;

// Normalize + validate a trip; open trips get exit = referenceDate.
const normalizeTrips = (trips, referenceISO) => {
  return (trips || [])
    .filter((t) => t && t.entry)
    .map((t) => ({
      id: t.id,
      entry: t.entry,
      exit: t.exit || referenceISO,
      isOpen: !t.exit,
      raw: t,
    }))
    .filter((t) => daysBetween(t.entry, t.exit) >= 0)
    .sort((a, b) => (a.entry < b.entry ? -1 : a.entry > b.entry ? 1 : 0));
};

// Merge overlapping / adjacent intervals so that days are counted only once.
const mergeIntervals = (norm) => {
  const merged = [];
  for (const t of norm) {
    const last = merged[merged.length - 1];
    if (last && t.entry <= addDays(last.exit, 1)) {
      if (t.exit > last.exit) last.exit = t.exit;
    } else {
      merged.push({ entry: t.entry, exit: t.exit });
    }
  }
  return merged;
};

// Count total days that fall within [windowStart, windowEnd] (both inclusive).
const daysInWindow = (intervals, windowStart, windowEnd) => {
  let total = 0;
  for (const iv of intervals) {
    if (iv.exit < windowStart || iv.entry > windowEnd) continue;
    const s = iv.entry > windowStart ? iv.entry : windowStart;
    const e = iv.exit < windowEnd ? iv.exit : windowEnd;
    total += daysBetween(s, e) + 1;
  }
  return total;
};

// Quick helper used by binary searches.
const quickDaysUsed = (trips, referenceISO) => {
  const norm = normalizeTrips(trips, referenceISO);
  const merged = mergeIntervals(norm);
  const windowStart = addDays(referenceISO, -(WINDOW_DAYS - 1));
  return daysInWindow(merged, windowStart, referenceISO);
};

// Detect if two normalized intervals overlap in date space.
const detectOverlaps = (norm) => {
  const overlaps = [];
  for (let i = 0; i < norm.length; i++) {
    for (let j = i + 1; j < norm.length; j++) {
      const a = norm[i];
      const b = norm[j];
      if (a.entry <= b.exit && b.entry <= a.exit) {
        overlaps.push([a.id, b.id]);
      }
    }
  }
  return overlaps;
};

/**
 * Main calculator.
 * @param {Array} trips
 * @param {string} referenceISO YYYY-MM-DD
 * @returns rich result object
 */
export function calculate(trips, referenceISO) {
  const windowStart = addDays(referenceISO, -(WINDOW_DAYS - 1));
  const windowEnd = referenceISO;

  const norm = normalizeTrips(trips, referenceISO);
  const merged = mergeIntervals(norm);

  const daysUsed = daysInWindow(merged, windowStart, windowEnd);
  const remaining = Math.max(0, MAX_DAYS - daysUsed);
  const overage = Math.max(0, daysUsed - MAX_DAYS);

  let status = "ok";
  if (overage > 0) status = "overage";
  else if (remaining <= 20) status = "warning";

  // Per-trip usage (how many days each trip contributes to the current window).
  const perTripUsage = norm.map((t) => {
    if (t.exit < windowStart || t.entry > windowEnd) {
      return {
        id: t.id,
        entry: t.entry,
        exit: t.raw.exit,
        isOpen: t.isOpen,
        daysCounted: 0,
        totalDays: daysBetween(t.entry, t.exit) + 1,
      };
    }
    const s = t.entry > windowStart ? t.entry : windowStart;
    const e = t.exit < windowEnd ? t.exit : windowEnd;
    return {
      id: t.id,
      entry: t.entry,
      exit: t.raw.exit,
      isOpen: t.isOpen,
      daysCounted: daysBetween(s, e) + 1,
      totalDays: daysBetween(t.entry, t.exit) + 1,
    };
  });

  const overlaps = detectOverlaps(norm);

  // Overage insights: when did the overage start and when can the traveler re-enter?
  let firstOverageDay = null;
  let canReenterAt = null;
  if (overage > 0) {
    const { firstOverage, canReenter } = computeOverageInfo(norm, referenceISO);
    firstOverageDay = firstOverage;
    canReenterAt = canReenter;
  }

  // Currently inside Schengen at referenceDate?
  const activeTrip = (trips || []).find(
    (t) =>
      t.entry &&
      t.entry <= referenceISO &&
      (!t.exit || t.exit >= referenceISO),
  );

  // Compute the latest legal exit date, considering the sliding 180-day
  // window. Works whether or not there is an open trip in progress:
  //   - active trip: extend its exit into the future
  //   - no active trip: hypothetically enter today and see how long we could
  //     stay continuously (equivalent to `maxStayFrom` semantics)
  let lastAllowedExit = null;
  if (overage === 0) {
    let lo = 0;
    let hi = WINDOW_DAYS;
    let best = -1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const testDate = addDays(referenceISO, mid);
      const testTrips = activeTrip
        ? trips.map((t) =>
            t === activeTrip ? { ...t, exit: testDate } : t,
          )
        : [
            ...(trips || []),
            { id: "__hyp__", entry: referenceISO, exit: testDate },
          ];
      const du = quickDaysUsed(testTrips, testDate);
      if (du <= MAX_DAYS) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (best >= 0) lastAllowedExit = addDays(referenceISO, best);
  }

  return {
    referenceDate: referenceISO,
    windowStart,
    windowEnd,
    daysUsed,
    remaining,
    overage,
    status,
    perTripUsage,
    overlaps,
    activeTrip: activeTrip || null,
    lastAllowedExit,
    firstOverageDay,
    canReenterAt,
  };
}

/**
 * Given already-normalized trips (from normalizeTrips), find:
 *  - firstOverage: earliest day the sliding-window count exceeded 90
 *  - canReenter: earliest day >= referenceDate when the traveler could re-enter
 *                (i.e. sliding-window count of PAST stays over [D-179..D-1] is <= 89)
 */
function computeOverageInfo(norm, referenceISO) {
  const merged = mergeIntervals(norm);
  const stayArr = [];
  for (const iv of merged) {
    let cur = iv.entry;
    while (cur <= iv.exit) {
      stayArr.push(cur);
      cur = addDays(cur, 1);
    }
  }
  stayArr.sort();

  // Find first overage day using sliding window (two-pointer since sorted)
  let firstOverage = null;
  let lo = 0;
  for (let i = 0; i < stayArr.length; i++) {
    const D = stayArr[i];
    const wStart = addDays(D, -(WINDOW_DAYS - 1));
    while (stayArr[lo] < wStart) lo++;
    const count = i - lo + 1;
    if (count > MAX_DAYS) {
      firstOverage = D;
      break;
    }
  }

  // Find earliest re-entry date on/after referenceISO
  let canReenter = null;
  // Cap search at ~1 window ahead of the last stay day
  const lastStay = stayArr[stayArr.length - 1];
  const searchCap = lastStay
    ? addDays(lastStay, WINDOW_DAYS + 30)
    : referenceISO;
  let D = referenceISO;
  while (D <= searchCap) {
    const wStart = addDays(D, -(WINDOW_DAYS - 1));
    const wPrev = addDays(D, -1);
    // Count stays in [wStart, wPrev]
    let count = 0;
    for (const s of stayArr) {
      if (s < wStart) continue;
      if (s > wPrev) break;
      count++;
    }
    if (count <= MAX_DAYS - 1) {
      canReenter = D;
      break;
    }
    D = addDays(D, 1);
  }

  return { firstOverage, canReenter };
}

/**
 * Given a planned trip [plannedEntry, plannedExit] and existing trips,
 * report whether it fits within the 90/180 rule (evaluated at plannedExit).
 */
export function evaluatePlannedTrip(trips, plannedEntry, plannedExit) {
  if (!plannedEntry || !plannedExit) return null;
  if (plannedExit < plannedEntry) {
    return { valid: false, reason: "exit_before_entry" };
  }
  const combined = [
    ...(trips || []),
    { id: "__planned__", entry: plannedEntry, exit: plannedExit },
  ];
  const result = calculate(combined, plannedExit);
  const plannedDays = daysBetween(plannedEntry, plannedExit) + 1;
  return {
    valid: result.overage === 0,
    plannedDays,
    daysUsed: result.daysUsed,
    remaining: result.remaining,
    overage: result.overage,
    referenceDate: plannedExit,
    windowStart: result.windowStart,
  };
}

/**
 * From a given start date, what is the maximum contiguous stay (in days)
 * that would still keep the traveler within the 90-day limit?
 */
export function maxStayFrom(trips, startISO) {
  let lo = 0;
  let hi = MAX_DAYS;
  let best = 0;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (mid === 0) {
      best = 0;
      lo = 1;
      continue;
    }
    const testExit = addDays(startISO, mid - 1);
    const testTrips = [
      ...(trips || []),
      { id: "__test__", entry: startISO, exit: testExit },
    ];
    const du = quickDaysUsed(testTrips, testExit);
    if (du <= MAX_DAYS) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/**
 * Detailed breakdown of the max-stay computation for a planned start.
 * Returns { days, exitDate, windowStart, windowEnd, existingAtStart,
 *           totalInWindow, freedByShift }
 *  - days: max continuous stay length (same as maxStayFrom).
 *  - exitDate: last legal day of that stay.
 *  - windowStart / windowEnd: the 180-day window on exitDate.
 *  - existingAtStart: total stay days already inside the 180-day window that
 *    ends on startISO (i.e. what would be counted "on entry day").
 *  - totalInWindow: total counted days (merged with the planned trip) in the
 *    window ending on exitDate. Guaranteed ≤ 90.
 *  - freedByShift: how many days from existingAtStart have fallen out of the
 *    window between startISO and exitDate (window slid forward).
 */
export function maxStayBreakdown(trips, startISO) {
  const days = maxStayFrom(trips, startISO);
  const exitDate = addDays(startISO, Math.max(0, days - 1));
  const windowEnd = exitDate;
  const windowStart = addDays(windowEnd, -(WINDOW_DAYS - 1));

  // Total days in the sliding window at exit — including the planned stay
  // MERGED with any existing (open) trip that overlaps. This is the number the
  // algorithm actually checks against 90.
  const withPlanned = days > 0
    ? [...(trips || []), { id: "__mp__", entry: startISO, exit: exitDate }]
    : trips || [];
  const totalInWindow = quickDaysUsed(withPlanned, exitDate);

  // Days present in the window ending on startISO (i.e. what would be counted
  // on the entry day if the user were to check in on that date).
  const normStart = normalizeTrips(trips, startISO);
  const mergedStart = mergeIntervals(normStart);
  const startWindowStart = addDays(startISO, -(WINDOW_DAYS - 1));
  const existingAtStart = daysInWindow(mergedStart, startWindowStart, startISO);

  // How many of those "existing at start" days have slid out of the window by
  // the exit date? Count existing (non-planned) days that fall within the OLD
  // window range [startWindowStart, exitWindowStart-1].
  const droppedOut = daysInWindow(
    mergedStart,
    startWindowStart,
    addDays(windowStart, -1),
  );

  return {
    days,
    exitDate,
    windowStart,
    windowEnd,
    existingAtStart,
    totalInWindow,
    freedByShift: droppedOut,
  };
}

/**
 * Build a Set of ISO date strings representing every day of stay (union of trips)
 * intersected with [rangeStart, rangeEnd]. Used by Calendar view for highlighting.
 */
export function stayDaySet(trips, rangeStart, rangeEnd, referenceISO) {
  const norm = normalizeTrips(trips, referenceISO);
  const merged = mergeIntervals(norm);
  const set = new Set();
  for (const iv of merged) {
    if (iv.exit < rangeStart || iv.entry > rangeEnd) continue;
    const s = iv.entry > rangeStart ? iv.entry : rangeStart;
    const e = iv.exit < rangeEnd ? iv.exit : rangeEnd;
    let cur = s;
    while (cur <= e) {
      set.add(cur);
      cur = addDays(cur, 1);
    }
  }
  return set;
}

/**
 * Return the set of days in [rangeStart, rangeEnd] where the traveler was
 * inside Schengen AND the sliding-window count strictly exceeds 90.
 */
export function overageDaySet(trips, rangeStart, rangeEnd, referenceISO) {
  const norm = normalizeTrips(trips, referenceISO);
  const merged = mergeIntervals(norm);
  const stayArr = [];
  for (const iv of merged) {
    let cur = iv.entry;
    while (cur <= iv.exit) {
      stayArr.push(cur);
      cur = addDays(cur, 1);
    }
  }
  stayArr.sort();
  const overSet = new Set();
  let lo = 0;
  for (let i = 0; i < stayArr.length; i++) {
    const D = stayArr[i];
    const wStart = addDays(D, -(WINDOW_DAYS - 1));
    while (stayArr[lo] < wStart) lo++;
    const count = i - lo + 1;
    if (count > MAX_DAYS && D >= rangeStart && D <= rangeEnd) overSet.add(D);
  }
  return overSet;
}

export const CONSTANTS = { WINDOW_DAYS, MAX_DAYS };
